import addMinutes from 'date-fns/add_minutes';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import first from 'lodash/first';
import { action, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, {
    ChangeEvent,
    PropsWithChildren,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import ReactDateTimeStyle from 'react-datetime/css/react-datetime.css';
import { deserialize, serialize, update } from 'serializr';
import TelInput from '@vromo/dashboard/src/common/components/ControlledTelInput/ControlledTelInput';
import useStylesheets from '@vromo/hooks/useStylesheets';
import { Contact, EntityList, Job, LineItem, Site } from '@vromo/models';
import Alert from '@vromo/ui/components/Alert';
import Checkbox from '@vromo/ui/components/Checkbox';
import Flex from '@vromo/ui/components/Flex';
import Geocoder, { GeocoderValue } from '@vromo/ui/components/Geocoder2';
import Input, { InputLabel, TextArea } from '@vromo/ui/components/Input';
import Table from '@vromo/ui/components/Table';
import { useTranslation } from 'react-i18next';
import LineItemComponent from './components/LineItem';
import OrderValue from './components/OrderValue';
import PickupTime from './components/PickupTime';
import styles, { AddButton, FormWithLabelMargins } from './jobForm.style';
import { qaLabelDispatch } from './../../../common-qa-labels/dispatch';

type Props = PropsWithChildren<{
    callingCode?: string;
    country?: string;
    currencyCode?: string;
    site: Site;
}>;

export const DEFAULT_PREP_TIME = 15;

export interface JobForm {
    reset(): void;
    submit(): Job;
    validate(): boolean;
}

const JobForm = observer<Props, JobForm>(
    ({ callingCode, children, country, currencyCode, site }, form) => {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        useStylesheets(ReactDateTimeStyle);

        const [job, setJob] = useState<Job>(new Job());

        const [dropoff, setDropoff] = useState<GeocoderValue | null>(null);
        const [deliveryInstructions, setDeliveryInstructions] = useState<string>('');
        const [orderValue, setOrderValue] = useState(0);
        const [orderValueVisible, setOrderValueVisible] = useState(false);
        const [orderCurrency, setOrderCurrency] = useState<string>();

        const [prepTime, setPrepTime] = useState<number | undefined>(DEFAULT_PREP_TIME);
        const [scheduledDate, setScheduledDate] = useState<number>();
        const [formErrors, setFormErrors] = useState<Array<any>>([]);
        const { t } = useTranslation();
        const ref = useRef<HTMLFormElement>(null);

        const controlledContent = 'Restricted items (alcohol, tobacco, other)';

        useEffect(() => {
            setOrderCurrency(currencyCode);
        }, [currencyCode]);

        const resetForm = (): void => {
            setJob(deserialize(Job, { name: '', contact: { name: '', email: '' } }));
            setDropoff(null);
            setDeliveryInstructions('');
            setScheduledDate(undefined);
            setPrepTime(DEFAULT_PREP_TIME);
            setOrderValue(0);
            setOrderCurrency(currencyCode);
            setOrderValueVisible(false);
        };

        const handleRestrictedItemsCheckboxClick = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.checked) {
                job.addControlledContents(controlledContent);
            } else {
                job.removeControlledContents(controlledContent);
            }
        };

        const handleAddItem = () =>
            runInAction(() => {
                if (!job.lines) {
                    job.lines = new EntityList(LineItem);
                }

                const newItem = new LineItem({ quantity: 1 });
                job.lines.push(newItem);
            });

        const formIsValid = (): boolean => {
            const errors = [];

            job.contact?.phone?.length &&
                !parsePhoneNumberFromString(job.contact?.phone)?.isPossible() &&
                errors.push({ field: 'phone', message: t('jobRequestForm.phoneNumber.error') });

            setFormErrors(errors);

            return (ref.current?.reportValidity() ?? false) && errors.length === 0;
        };

        const getSiteJob = () => {
            if (!formIsValid()) {
                throw new Error('Errors in form');
            }
            const {
                value: { lon, lat },
                label: dropOffAddress
            } = dropoff!;

            const by = prepTime
                ? addMinutes(new Date(), prepTime).valueOf()
                : scheduledDate?.valueOf();

            runInAction(() => {
                update(job, {
                    name: job.name.length ? job.name : dropOffAddress,
                    site: { id: site?.id },
                    instructions: deliveryInstructions,
                    ...(orderValue &&
                        !isNaN(orderValue * 100) && {
                            payable: {
                                currency: orderCurrency?.toLowerCase(),
                                order: orderValue * 100
                            }
                        }),
                    tasks: {
                        items: [
                            { zone: null, ...(by && { timeframe: { by } }) },
                            {
                                zone: {
                                    address: dropOffAddress,
                                    coords: { lat, lon },
                                    name: dropOffAddress
                                }
                            }
                        ]
                    }
                });
            });

            const serializedJob = serialize(job);

            delete serializedJob.progress;
            delete serializedJob.status;
            delete serializedJob.cancelled;

            return serializedJob;
        };

        useImperativeHandle(form, () => ({
            reset: resetForm,
            submit: getSiteJob,
            validate: formIsValid
        }));

        const setPhone = (phone: string) =>
            runInAction(() => {
                if (!job.contact) {
                    job.contact = new Contact();
                }
                job.contact.setPhone(phone);
            });

        const setEmail = (email: string) =>
            runInAction(() => {
                if (!job.contact) {
                    job.contact = new Contact();
                }
                job.contact.email = email;
            });

        const setOptedInForCampaignSms = (isOptedIn: boolean) =>
            runInAction(() => (job.optedInForCampaignSms = isOptedIn));

        const setName = (name: string) =>
            runInAction(() => {
                if (!job.contact) {
                    job.contact = new Contact();
                }
                job.contact.name = name;
            });

        return (
            <FormWithLabelMargins ref={ref} style={styles.container}>
                {formErrors.length > 0 && <Alert danger>{first(formErrors)?.message}</Alert>}
                {children}
                <Input
                    optional
                    data-testid={qaLabelDispatch.newJobModal.customerNameInput}
                    label={t('jobRequestForm.name.label')}
                    onChange={(e) => setName(e.target.value)}
                    optional
                    placeholder={t('jobRequestForm.name.placeholder')}
                    type="text"
                    value={job.contact?.name || ''}
                />
                <Flex vertical="bottom">
                    <InputLabel optional label={t('jobRequestForm.phoneNumber.label')}>
                        <TelInput
                            default={callingCode}
                            defaultCountry={country}
                            onChange={(number) => setPhone(number)}
                            value={job.contact?.phone || ''}
                        />
                    </InputLabel>
                    <div style={styles.inputDivider} />
                    <Input
                        data-testid={qaLabelDispatch.newJobModal.emailInput}
                        label={t('jobRequestForm.email.label')}
                        onChange={(e) => setEmail(e.target.value)}
                        optional
                        placeholder={t('jobRequestForm.email.placeholder')}
                        type="text"
                        value={job.contact?.email || undefined}
                    />
                </Flex>
                <Checkbox
                    labelStyle={styles.smsCheckboxLabel}
                    checked={job.optedInForCampaignSms}
                    onChange={() => setOptedInForCampaignSms(!job.optedInForCampaignSms)}
                    label={t('jobRequestForm.optedInForCampaignSms')}
                />
                <span style={styles.optedInForCampaignSmsDescription}>
                    {t('jobRequestForm.optedInForCampaignSmsDescription')}
                </span>

                <Flex vertical="bottom">
                    <Input
                        data-testid={qaLabelDispatch.newJobModal.orderNumberInput}
                        label={t('jobRequestForm.orderNumberJobName.label')}
                        onChange={action(
                            (e: ChangeEvent<HTMLInputElement>) => (job.name = e.target.value)
                        )}
                        optional
                        placeholder={t('jobRequestForm.orderNumberJobName.placeholder')}
                        type="text"
                        value={job.name}
                    />
                    {orderValueVisible ? null : (
                        <AddButton
                            data-testid={qaLabelDispatch.newJobModal.addOrderValueBtn}
                            onClick={() => setOrderValueVisible(true)}
                            style={styles.orderValueButton}>
                            {t('job.addOrderValue')}
                        </AddButton>
                    )}
                </Flex>
                {orderValueVisible && (
                    <OrderValue
                        {...{
                            orderCurrency,
                            orderValue,
                            setOrderCurrency,
                            setOrderValue
                        }}
                    />
                )}
                <PickupTime {...{ scheduledDate, prepTime, setScheduledDate, setPrepTime }} />
                <InputLabel label={t('lineItems.lineItems')}></InputLabel>
                {job.lines?.length ? (
                    <Table style={styles.itemList}>
                        <tbody>
                            {job.lines?.map((item) => (
                                <LineItemComponent
                                    item={item}
                                    onDescriptionChange={action(
                                        ({ target }) => (item.title = target.value)
                                    )}
                                    onQuantityChange={action(
                                        ({ target }) =>
                                            (item.quantity = Number.parseInt(target.value))
                                    )}
                                    onRemove={action(() => job?.lines?.items.remove(item))}
                                />
                            ))}
                        </tbody>
                    </Table>
                ) : null}
                <AddButton
                    data-testid={qaLabelDispatch.newJobModal.addItemBtn}
                    type="button"
                    onClick={handleAddItem}>
                    {t('lineItems.addItem')}
                </AddButton>
                <Checkbox
                    data-testid={qaLabelDispatch.newJobModal.restrictedItemsCheckbox}
                    label={t('jobRequestForm.restrictedItems')}
                    onChange={handleRestrictedItemsCheckboxClick}
                    checked={job.hasControlledContents(controlledContent)}
                />
                <InputLabel
                    data-testid={qaLabelDispatch.newJobModal.dropOffAddressLabel}
                    required
                    label={t('jobRequestForm.dropOffAddress.label')}>
                    <div style={styles.dropOffInputLabelDivider} />
                    <Geocoder
                        data-testid={qaLabelDispatch.newJobModal.dropOffAddressInput}
                        required
                        value={dropoff}
                        placeholder={t('jobRequestForm.dropOffAddress.placeholder')}
                        onChange={setDropoff}
                        proximity={site.coords}
                    />
                </InputLabel>
                <div style={styles.instructionsContainer}>
                    <TextArea
                        data-testid={qaLabelDispatch.newJobModal.deliveryInstructionsTextBox}
                        optional
                        label={t('jobRequestForm.deliveryInstructions')}
                        value={deliveryInstructions}
                        maxLength={350}
                        style={styles.instructionsInput}
                        onChange={(e) => setDeliveryInstructions(e.target.value)}
                    />
                </div>
            </FormWithLabelMargins>
        );
    },
    { forwardRef: true }
);

export default JobForm;
